import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileDown, Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientConsumption } from "@/components/ClientConsumption";
import { formatNum } from "@/lib/format";
import { createPdfDoc, withDefaults, finalizePdf } from "@/lib/pdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { datePresets } from "@/lib/dates";

/**
 * Reports — Coca-Cola facing. Cost-blind: NO revenue, NO target, NO %target, NO rupee.
 * Sales tab layout (B8 spec, One-Push audit item 1):
 *   • Top strip: Total days | Total cups | Avg per active day
 *   • Table: one row per (date, store) with cups sold. Missing days render '—'.
 *   • XLSX + PDF match on-screen exactly, honour date range + store filter.
 */

export default function ClientReports() {
  const pre = useMemo(datePresets, []);
  const [preset, setPreset] = useState("thisMonth");
  const [from, setFrom] = useState(pre.thisMonth.from);
  const [to, setTo] = useState(pre.thisMonth.to);
  const [storeId, setStoreId] = useState("");   // "" = all stores
  const [stores, setStores] = useState([]);
  const [dayRows, setDayRows] = useState([]);   // day-wise raw rows from server
  const [stats, setStats] = useState(null);     // CHANGE 2/3 (2026-02-29 audit) — backend stats block
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");                // filter within the day table (store or date)
  const [tab, setTab] = useState("sales");       // "sales" | "consumption"

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data)).catch(() => setStores([]));
  }, []);

  const applyPreset = (name) => {
    setPreset(name);
    if (name === "custom") return;
    const p = pre[name]; if (!p) return;
    setFrom(p.from); setTo(p.to);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: from, to_date: to };
      if (storeId) params.store_id = storeId;
      const { data } = await api.get("/client/sales-day-wise", { params });
      setDayRows(data.rows || []);
      setStats(data.stats || null);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [from, to, storeId]);
  useEffect(() => { load(); }, [load]);

  // Only actual sale-day rows are shown — no synthesized empty rows.
  // Enrich with store name and sort by date, then store_name.
  const actualRows = useMemo(() => {
    if (!dayRows) return [];
    const storeMap = new Map(stores.map(s => [s.id, s.store_name]));
    return dayRows
      .filter(r => (r.cups || 0) > 0)
      .map(r => ({
        date: r.date,
        store_id: r.store_id,
        store_name: r.store_name || storeMap.get(r.store_id) || "—",
        cups: r.cups,
      }))
      .sort((a, b) => (a.date === b.date ? a.store_name.localeCompare(b.store_name) : a.date.localeCompare(b.date)));
  }, [dayRows, stores]);

  const visibleRows = useMemo(() => {
    if (!q) return actualRows;
    const qq = q.toLowerCase();
    return actualRows.filter(r =>
      (r.store_name || "").toLowerCase().includes(qq) || r.date.includes(qq)
    );
  }, [actualRows, q]);

  const totals = useMemo(() => {
    // CHANGE 2 + 3 (2026-02-29 audit) — Use backend stats when available so Active Days matches
    // the days-since-live-start definition, not just "days with entries". Average Sales added.
    if (stats) {
      return {
        totalDays: stats.total_days || 0,
        totalCups: stats.total_cups || 0,
        activeDays: stats.active_days || 0,
        avgSales: stats.avg_sales || 0,
      };
    }
    // Fallback for old-shape responses
    const activeDates = new Set(actualRows.map(r => r.date));
    const totalCups = actualRows.reduce((a, r) => a + (r.cups || 0), 0);
    const totalDays = activeDates.size;
    return { totalDays, totalCups, activeDays: totalDays,
             avgSales: totalDays ? Math.round(totalCups / totalDays) : 0 };
  }, [actualRows, stats]);

  const rangeStr = `${from}_to_${to}`;

  const exportPDF = async () => {
    const doc = await createPdfDoc();
    doc.setFontSize(14); doc.setTextColor(225, 29, 42);
    doc.text("Coke@9 OCT · Day-wise Sales Report", 12, 12);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text("Powered by BBIPL for CCI", 12, 17);
    doc.setFontSize(8); doc.setTextColor(90);
    doc.text(`Range: ${from} to ${to}${storeId ? " · single store filter" : " · all stores"}`, 12, 22);
    // Top totals block
    autoTable(doc, withDefaults({
      startY: 26,
      head: [["Total days (active)", "Total cups", "Active days", "Average sales"]],
      body: [[totals.totalDays, formatNum(totals.totalCups), totals.activeDays,
              totals.activeDays > 0 ? formatNum(totals.avgSales) : "—"]],
    }));
    // Day × store rows — only actual sale-day rows
    autoTable(doc, withDefaults({
      startY: doc.lastAutoTable.finalY + 6,
      head: [["Date", "Store", "Cups sold"]],
      body: actualRows.map(r => [r.date, r.store_name, formatNum(r.cups)]),
    }));
    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`Coke9_Report_${rangeStr}.pdf`);
    toast.success("PDF downloaded");
  };

  const exportXLSX = async () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      [`Coke@9 OCT · Day-wise Sales Report`],
      [`Powered by BBIPL for CCI`],
      [`Range: ${from} to ${to}${storeId ? " · single store filter" : ""}`],
      [],
      ["Total days (active)", "Total cups", "Active days", "Average sales"],
      [totals.totalDays, totals.totalCups, totals.activeDays,
       totals.activeDays > 0 ? totals.avgSales : "—"],
      [],
      ["Date", "Store", "Cups sold"],
      ...actualRows.map(r => [r.date, r.store_name, r.cups]),
      [],
      ["", "", `Generated by Coke@9 OCT - powered By BBIPL`],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Day-wise Sales");
    XLSX.writeFile(wb, `Coke9_Report_${rangeStr}.xlsx`);
    toast.success("XLSX downloaded");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Reports" subtitle="Coca-Cola India · Fountain performance report" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-gray-100 rounded-lg p-1" data-testid="client-reports-tabs">
              <TabsTrigger data-testid="client-reports-tab-sales" value="sales"
                className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-4">
                Sales
              </TabsTrigger>
              <TabsTrigger data-testid="client-reports-tab-consumption" value="consumption"
                className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-4">
                Consumption
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Card className="p-4 rounded-xl border-gray-200 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Quick range</div>
                <Select value={preset} onValueChange={applyPreset}>
                  <SelectTrigger data-testid="report-preset-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7">Last 7 days</SelectItem>
                    <SelectItem value="thisMonth">This month</SelectItem>
                    <SelectItem value="lastMonth">Last month</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">From</div>
                <Input data-testid="report-from-input" type="date" value={from}
                  onChange={e => { setFrom(e.target.value); setPreset("custom"); }} className="h-10 mt-1" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">To</div>
                <Input data-testid="report-to-input" type="date" value={to}
                  onChange={e => { setTo(e.target.value); setPreset("custom"); }} className="h-10 mt-1" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store</div>
                <Select value={storeId || "__all__"} onValueChange={v => setStoreId(v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="report-store-select" className="h-10 mt-1"><SelectValue placeholder="All stores" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__">All stores</SelectItem>
                    {stores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.store_name} · {s.store_code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                {tab === "sales" && (
                  <>
                    <Button data-testid="report-pdf-btn" onClick={exportPDF} variant="outline">
                      <Download className="h-4 w-4 mr-2" /> PDF
                    </Button>
                    <Button data-testid="report-xlsx-btn" onClick={exportXLSX} className="bg-brand-red hover:bg-[#B31721]">
                      <FileDown className="h-4 w-4 mr-2" /> Excel (XLSX)
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative md:col-span-2">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="report-store-search" placeholder="Search store or date (YYYY-MM-DD)…"
                  value={q} onChange={e => setQ(e.target.value)} className="pl-9 h-10"
                  disabled={tab !== "sales"} />
              </div>
              {tab === "sales" && (
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 grid grid-cols-4 gap-2"
                     data-testid="sales-top-totals">
                  <div>
                    <span className="uppercase font-bold tracking-widest">Total days</span>
                    <div className="font-mono text-sm" data-testid="totals-days">{totals.totalDays}</div>
                  </div>
                  <div>
                    <span className="uppercase font-bold tracking-widest">Total cups</span>
                    <div className="font-mono text-sm" data-testid="totals-cups">{formatNum(totals.totalCups)}</div>
                  </div>
                  {/* CHANGE 2 (2026-02-29 audit) — Active Days = days since live-start (first attendance OR first sales/cups) */}
                  <div>
                    <span className="uppercase font-bold tracking-widest">Active days</span>
                    <div className="font-mono text-sm" data-testid="totals-active-days">{formatNum(totals.activeDays)}</div>
                  </div>
                  {/* CHANGE 3 (2026-02-29 audit) — Average Sales = Total Cups ÷ Active Days */}
                  <div>
                    <span className="uppercase font-bold tracking-widest">Average sales</span>
                    <div className="font-mono text-sm" data-testid="totals-avg-sales">
                      {totals.activeDays > 0 ? formatNum(totals.avgSales) : "—"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {tab === "consumption" && (
            <ClientConsumption from={from} to={to} storeId={storeId} variant="section" />
          )}

          {tab === "sales" && (
            <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-sales-daywise-table">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Store</th>
                      <th className="text-right px-4 py-3">Cups sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={3} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                    {!loading && visibleRows.length === 0 && (
                      <tr><td colSpan={3} className="text-center py-8 text-gray-500">No entries in this range</td></tr>
                    )}
                    {!loading && visibleRows.map((r, i) => (
                      <tr key={`${r.date}-${r.store_id}-${i}`} className="border-t border-gray-100"
                          data-testid={`daywise-row-${r.date}-${r.store_id}`}>
                        <td className="px-4 py-3 font-mono text-xs">{r.date}</td>
                        <td className="px-4 py-3">{r.store_name}</td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {formatNum(r.cups)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
